import { useEffect, useState } from "react";
import { Share2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMessage } from "@/lib/api";
import { Section, Empty } from "@/components/common";
import { selectCls } from "@/pages/Take5New";

export const RIGHT_LABEL = { VIEW: "Voir", USE: "Utiliser", INSPECT: "Inspecter", MANAGE: "Gérer" };
const TYPE_LABEL = { workplace: "Workplace", team: "Équipe", user: "Utilisateur" };
const HINT = { workplace: "Code d'invitation du workplace", user: "E-mail de l'utilisateur" };

export const SharesSection = ({ eq, onDone }) => {
  const [type, setType] = useState("team");
  const [target, setTarget] = useState("");
  const [right, setRight] = useState("INSPECT");
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/teams").then((r) => setTeams(r.data.filter((t) => t.active !== false))).catch(() => {}); }, []);
  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/equipment/${eq.id}/shares`, { target_type: type, target, right });
      onDone(r.data); setTarget(""); toast.success("Partage enregistré.");
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };
  const remove = async (sid) => {
    try { const r = await api.delete(`/equipment/${eq.id}/shares/${sid}`); onDone(r.data); toast.success("Partage retiré."); }
    catch (e) { toast.error(errMessage(e)); }
  };
  return (
    <Section title="Partage de l'équipement" icon={Share2} testId="equipment-shares">
      <p className="mb-3 text-sm text-muted-foreground">Votre workplace reste propriétaire et conserve tout l'historique, y compris les inspections faites par d'autres.</p>
      {!(eq.shares || []).length && <Empty testId="shares-empty">Aucun partage.</Empty>}
      <ul className="mb-4 space-y-2">
        {(eq.shares || []).map((s) => (
          <li key={s.id} data-testid={`share-${s.id}`} className="flex items-center justify-between gap-2 rounded-md border border-border p-2 text-sm">
            <span>{TYPE_LABEL[s.target_type]} · <b>{s.target_label}</b> · {RIGHT_LABEL[s.right]}</span>
            <Button variant="ghost" size="sm" data-testid={`share-remove-${s.id}`} onClick={() => remove(s.id)} aria-label="Retirer"><Trash2 className="h-4 w-4" /></Button>
          </li>
        ))}
      </ul>
      <div className="grid gap-2 sm:grid-cols-[150px_1fr_140px_auto]">
        <select data-testid="share-type-select" className={selectCls} value={type} onChange={(e) => { setType(e.target.value); setTarget(""); }}>
          {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {type === "team" ? (
          <select data-testid="share-target-team" className={selectCls} value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">— Choisir une équipe —</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        ) : <Input data-testid="share-target-input" className="h-12" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={HINT[type]} />}
        <select data-testid="share-right-select" className={selectCls} value={right} onChange={(e) => setRight(e.target.value)}>
          {Object.entries(RIGHT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Button data-testid="share-add-button" className="h-12" onClick={add} disabled={busy || !target}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Partager</Button>
      </div>
    </Section>
  );
};
